<template>
    <div>
        <div class="p-6 pb-0">
            <BreadCrumbs :navigation="navigation" />
        </div>

        <div class="p-6">
            <div class="bg-white shadow-md rounded-lg p-6 border border-gray-200 max-w-6xl mx-auto">
                <div class="flex justify-between items-center mb-6 border-b pb-4">
                    <h1 class="text-2xl font-bold text-gray-800">Rilevazione Ore su Commessa</h1>
                </div>

                <div class="space-y-6">

                    <!-- MODALITÀ SINGOLA -->
                    <template v-if="!allowMultiInsert">
                        <!-- Addetto (Solo Admin) -->
                        <div v-if="isAdmin || !$page.props.auth.user.worker" class="flex flex-col">
                            <label class="text-sm font-semibold text-gray-700 mb-1">Addetto <span class="text-red-500">*</span></label>
                            <select v-model="form.worker" class="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm p-2.5 border">
                                <option v-for="worker in workers" :key="worker.WorkerID" :value="worker.WorkerID">
                                    {{ worker.LastName }} {{ worker.Name }}
                                </option>
                            </select>
                        </div>

                        <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <!-- Commessa -->
                            <div class="flex flex-col">
                                <label class="text-sm font-semibold text-gray-700 mb-1">Commessa <span class="text-red-500">*</span></label>
                                <div class="relative w-full inline-flex items-center cursor-pointer" @click="openJobTable(0)">
                                    <span class="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm p-2.5 border bg-white">
                                        {{ rowJobDisplay(rows[0]) }}
                                    </span>
                                    <span class="absolute right-3 text-gray-500 pointer-events-none">
                                        <v-icon name="hi-arrows-expand" scale="1.2" />
                                    </span>
                                </div>
                            </div>

                            <!-- Operazione -->
                            <div class="flex flex-col">
                                <label class="text-sm font-semibold text-gray-700 mb-1">Operazione <span v-if="requireOperation" class="text-red-500">*</span></label>
                                <select v-model="rows[0].operation" class="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm p-2.5 border">
                                    <option value="" :disabled="requireOperation" selected>Seleziona operazione</option>
                                    <option v-for="op in operations" :key="op.OperationID" :value="op.OperationID">
                                        {{ op.Description }}
                                    </option>
                                </select>
                            </div>

                            <!-- Data -->
                            <div class="flex flex-col">
                                <label class="text-sm font-semibold text-gray-700 mb-1">Data <span class="text-red-500">*</span></label>
                                <input type="date" v-model="form.date" class="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm p-2.5 border">
                            </div>
                        </div>

                        <!-- Tempi -->
                        <div class="grid grid-cols-1 gap-6 border-t pt-4">
                            <template v-if="allowDetailedTime">
                                <div class="flex flex-col">
                                    <label class="text-sm font-semibold text-gray-700 mb-1">Range di ore <span class="text-red-500">*</span></label>
                                    <VueDatePicker v-model="rows[0].timeRange" range time-picker auto-apply @update:model-value="val => onTimeRangeChange(val, 0)" />
                                </div>
                            </template>
                            <div v-else class="flex flex-col">
                                <label class="text-sm font-semibold text-gray-700 mb-1">Durata <span class="text-red-500">*</span></label>
                                <VueDatePicker v-model="rows[0].durationPicker" time-picker auto-apply @update:model-value="val => onDurationChange(val, 0)" />
                            </div>
                        </div>

                        <!-- Note -->
                        <div class="flex flex-col border-t pt-4">
                            <label class="text-sm font-semibold text-gray-700 mb-1">Info attività</label>
                            <textarea
                                v-model="rows[0].notes"
                                maxlength="32"
                                rows="2"
                                class="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm p-2.5 border"
                                :placeholder="$t('Massimo 32 caratteri')"
                            ></textarea>
                            <div class="text-right">
                                <span class="text-xs text-gray-500">{{ rows[0].notes.length }}/32</span>
                            </div>
                        </div>
                    </template>

                    <!-- MODALITÀ MULTIPLA -->
                    <template v-else>
                        <!-- Campi costanti: Addetto e Data -->
                        <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <!-- Addetto (Solo Admin) -->
                            <div v-if="isAdmin || !$page.props.auth.user.worker" class="flex flex-col">
                                <label class="text-sm font-semibold text-gray-700 mb-1">Addetto <span class="text-red-500">*</span></label>
                                <select v-model="form.worker" class="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm p-2.5 border">
                                    <option v-for="worker in workers" :key="worker.WorkerID" :value="worker.WorkerID">
                                        {{ worker.LastName }} {{ worker.Name }}
                                    </option>
                                </select>
                            </div>

                            <!-- Data -->
                            <div class="flex flex-col">
                                <label class="text-sm font-semibold text-gray-700 mb-1">Data <span class="text-red-500">*</span></label>
                                <input type="date" v-model="form.date" class="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm p-2.5 border">
                            </div>
                        </div>

                        <!-- Righe rilevazioni -->
                        <div class="border-t pt-4">
                            <div class="flex justify-between items-center mb-3">
                                <h2 class="text-base font-semibold text-gray-700">Righe di rilevazione</h2>
                                <button
                                    @click="addRow"
                                    type="button"
                                    class="inline-flex items-center gap-1 px-3 py-1.5 text-sm border border-blue-500 text-blue-600 rounded-md hover:bg-blue-50 transition-colors"
                                >
                                    <v-icon name="co-plus" scale="0.9" /> Aggiungi riga
                                </button>
                            </div>

                            <div class="space-y-4">
                                <div
                                    v-for="(row, idx) in rows"
                                    :key="idx"
                                    class="border border-gray-200 rounded-lg p-4 bg-gray-50 relative"
                                >
                                    <div class="absolute top-2 right-2 flex items-center gap-1">
                                        <button
                                            v-if="rows.length > 1 && idx > 0"
                                            @click="moveRowUp(idx)"
                                            type="button"
                                            class="text-gray-400 hover:text-blue-500 transition-colors"
                                            title="Sposta su"
                                        >
                                            <v-icon name="md-keyboardarrowup-round" scale="1" />
                                        </button>
                                        <button
                                            v-if="rows.length > 1 && idx < rows.length - 1"
                                            @click="moveRowDown(idx)"
                                            type="button"
                                            class="text-gray-400 hover:text-blue-500 transition-colors"
                                            title="Sposta giù"
                                        >
                                            <v-icon name="md-keyboardarrowdown-round" scale="1" />
                                        </button>
                                        <button
                                            v-if="rows.length > 1"
                                            @click="removeRow(idx)"
                                            type="button"
                                            class="text-gray-400 hover:text-red-500 transition-colors ml-1"
                                            title="Rimuovi riga"
                                        >
                                            <v-icon name="fa-trash" scale="1" />
                                        </button>
                                    </div>

                                    <div class="grid grid-cols-1 md:grid-cols-12 gap-4">
                                        <!-- Commessa -->
                                        <div class="flex flex-col md:col-span-4">
                                            <label class="text-xs font-semibold text-gray-600 mb-1">Commessa <span class="text-red-500">*</span></label>
                                            <div class="relative w-full inline-flex items-center cursor-pointer" @click="openJobTable(idx)">
                                                <span class="block w-full rounded-md border-gray-300 shadow-sm sm:text-sm p-2.5 border bg-white truncate">
                                                    {{ rowJobDisplay(row) }}
                                                </span>
                                                <span class="absolute right-3 text-gray-500 pointer-events-none">
                                                    <v-icon name="hi-arrows-expand" scale="1" />
                                                </span>
                                            </div>
                                        </div>

                                        <!-- Operazione -->
                                        <div class="flex flex-col md:col-span-3">
                                            <label class="text-xs font-semibold text-gray-600 mb-1">Operazione <span v-if="requireOperation" class="text-red-500">*</span></label>
                                            <select v-model="row.operation" class="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm p-2.5 border bg-white">
                                                <option value="" :disabled="requireOperation" selected>Seleziona operazione</option>
                                                <option v-for="op in operations" :key="op.OperationID" :value="op.OperationID">
                                                    {{ op.Description }}
                                                </option>
                                            </select>
                                        </div>

                                        <!-- Tempi -->
                                        <div class="flex flex-col md:col-span-2">
                                            <label class="text-xs font-semibold text-gray-600 mb-1">
                                                {{ allowDetailedTime ? 'Range di ore' : 'Durata' }} <span class="text-red-500">*</span>
                                            </label>
                                            <VueDatePicker
                                                v-if="allowDetailedTime"
                                                v-model="row.timeRange"
                                                range
                                                time-picker
                                                auto-apply
                                                @update:model-value="val => onTimeRangeChange(val, idx)"
                                            />
                                            <VueDatePicker
                                                v-else
                                                v-model="row.durationPicker"
                                                time-picker
                                                auto-apply
                                                @update:model-value="val => onDurationChange(val, idx)"
                                            />
                                        </div>

                                        <!-- Note -->
                                        <div class="flex flex-col md:col-span-3">
                                            <label class="text-xs font-semibold text-gray-600 mb-1">Info attività</label>
                                            <input
                                                v-model="row.notes"
                                                type="text"
                                                maxlength="32"
                                                class="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm p-2.5 border bg-white"
                                                :placeholder="$t('Massimo 32 caratteri')"
                                            />
                                            <span class="text-right text-xs text-gray-400">{{ row.notes.length }}/32</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </template>

                    <!-- Totale ore (solo modalità multipla) -->
                    <div v-if="allowMultiInsert && totalRowsMinutes > 0" class="flex justify-end">
                        <div class="bg-blue-50 border border-blue-200 rounded-lg px-4 py-2 flex items-center gap-3">
                            <div class="text-blue-600">
                                <v-icon name="bi-clock-history" scale="1.2" />
                            </div>
                            <div>
                                <p class="text-[10px] text-blue-600 font-semibold uppercase tracking-wider leading-none mb-1">Tempo Totale</p>
                                <p class="text-xl font-bold text-blue-800 leading-none">{{ totalRowsFormatted }}</p>
                            </div>
                        </div>
                    </div>

                    <div class="pt-4 flex justify-end border-t">
                        <button
                            @click="handleSave"
                            :disabled="processing"
                            class="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 transition-colors"
                        >
                            <v-icon v-if="processing" name="md-sync" class="mr-2 animate-spin" />
                            {{ processing ? 'Salvataggio...' : (allowMultiInsert ? 'Salva Rilevazioni' : 'Salva Rilevazione') }}
                        </button>
                    </div>
                </div>
            </div>
        </div>

        <!-- Modal selezione commessa -->
        <div v-if="showJobTable" class="fixed inset-0 flex items-center z-50" :class="{ 'justify-end': embedded, 'justify-center': !embedded }">
            <div class="absolute inset-0 bg-black opacity-50" @click="showJobTable = false"></div>
            <div class="bg-white rounded-lg shadow-xl z-10 max-h-[80vh] overflow-y-auto modal-content" :class="{ 'w-9/12': embedded, 'w-11/12': !embedded }">
                <div class="px-6 py-4 border-b flex items-center justify-between bg-gray-50">
                    <h2 class="text-xl font-bold text-gray-800 flex items-center gap-2">
                        <v-icon name="co-plus" class="text-blue-600" scale="1.2" />
                        Seleziona commessa
                    </h2>
                </div>
                <div class="p-6 pt-3">
                    <PageServerTable
                        :headers="jobTableProps.headers"
                        :items="jobTableProps.items"
                        :items-length="jobTableProps.total"
                        :items-per-page="jobTableProps.ipp"
                        :items-per-page-options="jobTableProps.ippo"
                        :page="jobTableProps.page"
                        :sort-by="jobTableProps.sortby"
                        :reload-fn="loadJobs"
                        hover
                        @click:row="(e, { item }) => selectJob(item)"
                    />
                </div>
            </div>
        </div>

        <ToastNotification
            :show="toast.show"
            :message="toast.message"
            :type="toast.type"
            @update:show="toast.show = $event"
        />

        <ConfirmationModal :show="showConfirmSaveModal" @close="showConfirmSaveModal = false">
            <template #title>
                {{ $t('Conferma Salvataggio') }}
            </template>

            <template #content>
                <span v-if="allowMultiInsert && rows.length > 1">Confermi le rilevazioni specificate? ({{ rows.length }} righe.)</span>
                <span v-else>Confermi la rilevazione specificata?</span>
            </template>

            <template #footer>
                <button
                    @click="showConfirmSaveModal = false"
                    class="px-4 py-2 bg-gray-200 text-gray-800 rounded-md mr-2"
                >
                    {{ $t('Annulla') }}
                </button>
                <button
                    @click="confirmSave"
                    class="px-4 py-2 bg-blue-600 text-white rounded-md"
                >
                    {{ $t('Conferma') }}
                </button>
            </template>
        </ConfirmationModal>
    </div>
</template>

<script>
import { Link } from '@inertiajs/vue3';
import BreadCrumbs from '@/Components/BreadCrumbs.vue';
import PageServerTable from '@/Components/PageServerTable.vue';
import ToastNotification from '@/Components/ToastNotification.vue';
import ConfirmationModal from '@/Components/ConfirmationModal.vue';
import axios from 'axios';

function makeEmptyRow() {
    return {
        job: null,
        jobDescription: '',
        operation: '',
        timeRange: null,
        durationPicker: { hours: 1, minutes: 0 },
        fromTime: '',
        toTime: '',
        duration: '01:00',
        notes: ''
    };
}

export default {
    components: {
        Link,
        BreadCrumbs,
        PageServerTable,
        ToastNotification,
        ConfirmationModal
    },
    props: {
        allowDetailedTime: Boolean,
        allowMultiInsert: Boolean,
        requireOperation: Boolean,
        workers: Array,
        operations: Array
    },
    data() {
        return {
            processing: false,
            showJobTable: false,
            showConfirmSaveModal: false,
            activeRowIdx: null,
            jobTableProps: {
                headers: [
                    { title: 'Commessa', align: 'start', value: 'Job', key: 'MA_Jobs.Job', filterable: true, filtervalue: "" },
                    { title: 'Descrizione', align: 'start', value: 'Description', key: 'MA_Jobs.Description', filterable: true, filtervalue: "" },
                    { title: 'Ragione Sociale', align: 'start', value: 'RagSoc', key: 'MA_CustSupp.CompanyName', filterable: true, filtervalue: "" }
                ],
                items: [],
                total: 0,
                ipp: 10,
                ippo: [
                    { "value": 10, "title": '10' },
                    { "value": 25, "title": '25' },
                    { "value": 50, "title": '50' },
                    { "value": 100, "title": '100' }
                ],
                page: 1,
                sortby: [{ key: 'MA_Jobs.Job', order: 'asc' }]
            },
            toast: {
                show: false,
                message: '',
                type: 'success'
            },
            form: {
                worker: this.$page.props.auth.user.worker,
                date: new Date().toISOString().split('T')[0],
                latitude: 0,
                longitude: 0,
                accuracy: 0
            },
            rows: [makeEmptyRow()]
        };
    },
    computed: {
        isAdmin() {
            return this.$page.props.isAdminOrSuperAdmin;
        },
        totalRowsMinutes() {
            let total = 0;
            for (const row of this.rows) {
                if (this.allowDetailedTime) {
                    if (row.fromTime && row.toTime) {
                        const [fh, fm] = row.fromTime.split(':').map(Number);
                        const [th, tm] = row.toTime.split(':').map(Number);
                        total += Math.abs((th * 60 + tm) - (fh * 60 + fm));
                    }
                } else {
                    if (row.duration) {
                        const [h, m] = row.duration.split(':').map(Number);
                        total += h * 60 + m;
                    }
                }
            }
            return total;
        },
        totalRowsFormatted() {
            const h = Math.floor(this.totalRowsMinutes / 60);
            const m = this.totalRowsMinutes % 60;
            return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
        },
        navigation() {
            return [
                {
                    name: 'Dashboard',
                    href: route('dashboard'),
                },
                {
                    name: this.$t('Rilevazione Commesse'),
                    href: route("modules.home", "rilevazione-commesse"),
                },
                {
                    name: this.$t('Rilevazione Ore su Commessa'),
                    href: route('modules.rcomm.rilevazione-ora-su-commessa'),
                }
            ];
        }
    },
    methods: {
        showToast(message, type = 'success') {
            this.toast.message = message;
            this.toast.type = type;
            this.toast.show = true;
        },

        rowJobDisplay(row) {
            if (!row.job) return 'Seleziona commessa';
            return row.jobDescription ? `${row.job} - ${row.jobDescription}` : row.job;
        },

        addRow() {
            this.rows.push(makeEmptyRow());
        },

        moveRowUp(idx) {
            if (idx > 0) {
                const row = this.rows[idx];
                this.rows.splice(idx, 1);
                this.rows.splice(idx - 1, 0, row);
            }
        },

        moveRowDown(idx) {
            if (idx < this.rows.length - 1) {
                const row = this.rows[idx];
                this.rows.splice(idx, 1);
                this.rows.splice(idx + 1, 0, row);
            }
        },

        removeRow(idx) {
            this.rows.splice(idx, 1);
        },

        onTimeRangeChange(val, idx) {
            if (val && val.length === 2) {
                this.rows[idx].fromTime = `${String(val[0].hours).padStart(2, '0')}:${String(val[0].minutes).padStart(2, '0')}`;
                this.rows[idx].toTime = `${String(val[1].hours).padStart(2, '0')}:${String(val[1].minutes).padStart(2, '0')}`;
            } else {
                this.rows[idx].fromTime = '';
                this.rows[idx].toTime = '';
            }
        },

        onDurationChange(val, idx) {
            if (val) {
                this.rows[idx].duration = `${String(val.hours).padStart(2, '0')}:${String(val.minutes).padStart(2, '0')}`;
            } else {
                this.rows[idx].duration = '00:00';
            }
        },

        openJobTable(idx) {
            this.activeRowIdx = idx;
            this.loadJobs();
        },

        async loadJobs(sortby, itemsperpage, page) {
            try {
                const res = await axios.post(route('modules.rcomm.get-jobs'), {
                    headers: this.jobTableProps.headers,
                    sortby: Array.isArray(sortby) ? sortby : this.jobTableProps.sortby,
                    itemsperpage: typeof itemsperpage === 'number' ? itemsperpage : this.jobTableProps.ipp,
                    page: typeof page === 'number' ? page : this.jobTableProps.page
                });

                this.jobTableProps.items = res.data.jobs;
                this.jobTableProps.total = res.data.total;
                this.jobTableProps.ipp = res.data.ipp;
                this.jobTableProps.page = res.data.page;
                this.jobTableProps.headers = res.data.headers;
                this.jobTableProps.sortby = res.data.sortby;

                this.showJobTable = true;
            } catch (e) {
                console.error("Errore caricamento commesse:", e);
            }
        },

        selectJob(job) {
            if (this.activeRowIdx !== null) {
                this.rows[this.activeRowIdx].job = job.Job;
                this.rows[this.activeRowIdx].jobDescription = job.Description;
            }
            this.showJobTable = false;
            this.activeRowIdx = null;
        },

        handleSave() {
            if (!this.form.worker) {
                this.showToast('Selezionare un addetto', 'warning');
                return;
            }
            if (!this.form.date) {
                this.showToast('Selezionare una data', 'warning');
                return;
            }

            for (let i = 0; i < this.rows.length; i++) {
                const row = this.rows[i];
                const rowNum = i + 1;
                if (!row.job) {
                    this.showToast(`Riga ${rowNum}: selezionare una commessa`, 'warning');
                    return;
                }
                if (this.requireOperation && !row.operation) {
                    this.showToast(`Riga ${rowNum}: selezionare un'operazione`, 'warning');
                    return;
                }
                if (this.allowDetailedTime && (!row.fromTime || !row.toTime)) {
                    this.showToast(`Riga ${rowNum}: specificare il range di ore`, 'warning');
                    return;
                }
                if (!this.allowDetailedTime && !row.duration) {
                    this.showToast(`Riga ${rowNum}: specificare la durata`, 'warning');
                    return;
                }
            }

            if (this.allowDetailedTime && this.allowMultiInsert && this.rows.length > 1) {
                const toMin = t => { const [h, m] = t.split(':').map(Number); return h * 60 + m; };
                const ranges = this.rows.map((r, i) => ({ i: i + 1, from: toMin(r.fromTime), to: toMin(r.toTime) }));
                for (let a = 0; a < ranges.length; a++) {
                    for (let b = a + 1; b < ranges.length; b++) {
                        if (ranges[a].from < ranges[b].to && ranges[b].from < ranges[a].to) {
                            this.showToast(`Le righe ${ranges[a].i} e ${ranges[b].i} hanno orari sovrapposti`, 'warning');
                            return;
                        }
                    }
                }
            }

            if (navigator.geolocation) {
                navigator.geolocation.getCurrentPosition((position) => {
                    this.form.latitude = position.coords.latitude;
                    this.form.longitude = position.coords.longitude;
                    this.form.accuracy = position.coords.accuracy;
                }, (error) => {
                    console.warn("Geolocalizzazione non disponibile", error);
                });
            }

            this.showConfirmSaveModal = true;
        },

        confirmSave() {
            this.showConfirmSaveModal = false;
            this.saveData();
        },

        async saveData() {
            this.processing = true;
            try {
                const payload = {
                    worker: this.form.worker,
                    date: this.form.date,
                    latitude: this.form.latitude,
                    longitude: this.form.longitude,
                    accuracy: this.form.accuracy,
                    rows: this.rows.map(row => ({
                        job: row.job,
                        operation: row.operation,
                        fromTime: row.fromTime,
                        toTime: row.toTime,
                        duration: row.duration,
                        notes: row.notes
                    }))
                };

                const res = await axios.post(route('modules.rcomm.save-data'), payload);
                if (res.data.success) {
                    this.showToast('Salvato con successo', 'success');
                    setTimeout(() => {
                        location.reload();
                    }, 1500);
                }
            } catch (e) {
                this.showToast(e.response?.data?.errorMessage || 'Errore durante il salvataggio', 'error');
            } finally {
                this.processing = false;
            }
        }
    }
};
</script>
